import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, EyeOff, MessageSquareWarning, CheckCircle2, XCircle, Link as LinkIcon } from "lucide-react";

/**
 * Admin-only feed of incoming citizen reports with reveal + status workflow.
 */
export default function CitizenReportsAdmin() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [revealed, setRevealed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/admin/citizen-reports", { params });
      setReports(data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load reports");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/citizen-reports/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  const reveal = async (id) => {
    try {
      const { data } = await api.post(`/admin/citizen-reports/${id}/reveal`);
      setRevealed({ ...revealed, [id]: data.reporter_contact || "(blank)" });
      toast.success("Reporter identity revealed (audit logged)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reveal failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="citizen-reports-admin-root">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Admin · Citizen MUKTI</div>
        <h1 className="font-display font-bold text-3xl mt-1">Citizen reports feed</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Every anonymous observation submitted via the public Citizen MUKTI app. Reporter
          identity is kept confidential by default — reveal is one click and audit-logged.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "linked", "dismissed"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
            data-testid={`cr-filter-${s}`}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
        <div className="ml-auto text-xs font-mono text-slate-500">
          {reports.length} report{reports.length !== 1 ? "s" : ""}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-8 text-center"
             data-testid="cr-empty">
          No citizen reports yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {reports.map((r) => (
            <li key={r.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
                data-testid={`cr-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <MessageSquareWarning className="h-4 w-4 text-orange-600 shrink-0" />
                    <Badge
                      variant={r.status === "new" ? "default" : "outline"}
                      className="capitalize"
                    >
                      {r.status}
                    </Badge>
                    <span className="text-[11px] font-mono text-slate-400">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-slate-800">{r.concern}</div>
                  <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 font-mono">
                    {r.location && <span>📍 {r.location}</span>}
                    {r.when && <span>🕒 {r.when}</span>}
                    {r.phone && <span>☎ {r.phone}</span>}
                    {r.vehicle && <span>🚗 {r.vehicle}</span>}
                    {r.alias && <span>🪪 {r.alias}</span>}
                  </div>
                  {r.description && (
                    <div className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">
                      {r.description}
                    </div>
                  )}
                  <div className="mt-3 text-[11px] font-mono text-slate-400">
                    ID: {r.id}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {r.has_reporter_contact && (
                    revealed[r.id] ? (
                      <div className="text-xs bg-amber-50 border border-amber-300 rounded px-2 py-1 font-mono text-amber-900">
                        {revealed[r.id]}
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => reveal(r.id)}
                              data-testid={`cr-reveal-${r.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Reveal reporter
                      </Button>
                    )
                  )}
                  {!r.has_reporter_contact && (
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <EyeOff className="h-3.5 w-3.5" /> Anonymous
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button size="sm" variant="ghost"
                        onClick={() => setStatus(r.id, "linked")}
                        data-testid={`cr-link-${r.id}`}>
                  <LinkIcon className="h-3.5 w-3.5 mr-1" /> Mark linked
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => setStatus(r.id, "new")}
                        data-testid={`cr-reopen-${r.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Reopen
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                        onClick={() => setStatus(r.id, "dismissed")}
                        data-testid={`cr-dismiss-${r.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
